import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Phone,
  PhoneCall,
  GitBranch,
  Clock,
  FileText,
  Settings,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  User,
  Shield,
  Activity,
  Layers,
  PhoneForwarded,
  ScrollText,
  Lock,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import { systemInfo } from '../data/mockData';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { isConnected, statusChanges } = useRealtime();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-testid="notifications-button"]') && 
          !event.target.closest('[data-testid="user-menu-button"]')) {
        setNotificationsOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Add notifications from real-time status changes
  useEffect(() => {
    if (statusChanges && statusChanges.length > 0) {
      const latestChange = statusChanges[statusChanges.length - 1];
      const newNotification = {
        title: `Extension ${latestChange.extension} ${latestChange.online ? 'Online' : 'Offline'}`,
        message: latestChange.online ? 'User has registered their phone' : 'User has gone offline',
        time: new Date().toLocaleTimeString()
      };
      setNotifications(prev => [newNotification, ...prev].slice(0, 10));
    }
  }, [statusChanges]);

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  // All navigation items with admin requirement
  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, adminOnly: false },
    { name: 'Users', href: '/extensions', icon: Users, adminOnly: true },
    { name: 'Voice & Chat', href: '/voice-chat', icon: PhoneCall, adminOnly: true },
    { name: 'SIP Trunks', href: '/sip-trunks', icon: Phone, adminOnly: true },
    { name: 'Ring Groups', href: '/ring-groups', icon: GitBranch, adminOnly: true },
    { name: 'Call Queues', href: '/call-queues', icon: Layers, adminOnly: true },
    { name: 'Outbound Rules', href: '/outbound-rules', icon: PhoneForwarded, adminOnly: true },
    { name: 'Office Hours', href: '/office-hours', icon: Clock, adminOnly: true },
    { name: 'Call Logs', href: '/call-logs', icon: FileText, adminOnly: false },
    { name: 'Event Log', href: '/event-log', icon: ScrollText, adminOnly: true },
    { name: 'Reports', href: '/reports', icon: Activity, adminOnly: true },
    { name: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
  ];

  // Filter navigation based on user role
  const navigation = allNavigation.filter(item => !item.adminOnly || isAdmin());

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-[#12121a] border-r border-[#1e1e2d] transition-all duration-300 flex flex-col fixed h-full z-50`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#1e1e2d]">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">ELE145</h1>
                <p className="text-xs text-gray-500">{systemInfo.version}</p>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center mx-auto">
              <Phone className="w-5 h-5 text-white" />
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-[#1e1e2d] rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                      active
                        ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                        : 'text-gray-400 hover:bg-[#1e1e2d] hover:text-white'
                    }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-400' : 'text-gray-500 group-hover:text-white'}`} />
                    {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
          
          {/* Admin-only indicator for non-admins */}
          {!isAdmin() && sidebarOpen && (
            <div className="mt-4 px-3">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs font-medium text-yellow-400">Limited Access</span>
                </div>
                <p className="text-xs text-gray-500">
                  Contact admin for full access to all features.
                </p>
              </div>
            </div>
          )}
        </nav>

        {/* System Status */}
        {sidebarOpen && (
          <div className="p-4 border-t border-[#1e1e2d]">
            <div className="bg-[#1e1e2d] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'} rounded-full animate-pulse`}></div>
                <span className="text-xs text-gray-400">{isConnected ? 'Real-time Connected' : 'Connecting...'}</span>
              </div>
              <p className="text-xs text-gray-500">Uptime: {systemInfo.uptime}</p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Top Header */}
        <header className="h-16 bg-[#12121a] border-b border-[#1e1e2d] flex items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search..."
                className="bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 w-64"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Real-time Status Indicator */}
            <div className={`flex items-center gap-1 px-2 py-1 ${isConnected ? 'bg-green-500/20 border-green-500/30' : 'bg-red-500/20 border-red-500/30'} border rounded-full`}>
              {isConnected ? (
                <Wifi className="w-3 h-3 text-green-400" />
              ) : (
                <WifiOff className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs font-medium ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                {isConnected ? 'Live' : 'Offline'}
              </span>
            </div>

            {/* Role Badge */}
            {isAdmin() ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
                <Shield className="w-3 h-3 text-yellow-400" />
                <span className="text-xs font-medium text-yellow-400">Admin</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full">
                <User className="w-3 h-3 text-blue-400" />
                <span className="text-xs font-medium text-blue-400">User</span>
              </div>
            )}

            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 hover:bg-[#1e1e2d] rounded-lg transition-colors"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 text-gray-400" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg shadow-xl z-50">
                  <div className="px-4 py-3 border-b border-[#2a2a3d] flex justify-between items-center">
                    <h3 className="text-sm font-medium text-white">Notifications</h3>
                    {notifications.length > 0 && (
                      <button 
                        onClick={() => setNotifications([])}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No new notifications
                      </div>
                    ) : (
                      notifications.map((notif, index) => (
                        <div key={index} className="px-4 py-3 border-b border-[#2a2a3d] hover:bg-[#2a2a3d] cursor-pointer">
                          <p className="text-sm text-white">{notif.title}</p>
                          <p className="text-xs text-gray-400 mt-1">{notif.message}</p>
                          <p className="text-xs text-gray-500 mt-1">{notif.time}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-3 p-2 hover:bg-[#1e1e2d] rounded-lg transition-colors"
                data-testid="user-menu-button"
              >
                <div className={`w-8 h-8 ${isAdmin() ? 'bg-gradient-to-br from-yellow-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'} rounded-full flex items-center justify-center`}>
                  <span className="text-sm font-medium">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{isAdmin() ? 'Administrator' : 'User'}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg shadow-xl py-2 z-50">
                  <div className="px-4 py-2 border-b border-[#2a2a3d]">
                    <p className="text-sm text-white">{user?.email}</p>
                    <p className="text-xs text-gray-500">{isAdmin() ? 'Administrator' : 'Standard User'}</p>
                  </div>
                  <Link to="/profile" className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-[#2a2a3d]">
                    <User className="w-4 h-4" />
                    My Profile
                  </Link>
                  {isAdmin() && (
                    <Link to="/settings" className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-[#2a2a3d]">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  )}
                  <hr className="my-2 border-[#2a2a3d]" />
                  <button 
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-[#2a2a3d] w-full"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
